import { useState, useEffect } from 'react';
import JSZip from 'jszip';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Monitor, WifiOff, RefreshCw,
  Camera, Shield, Pencil,
  Layers, Download, Upload, CheckCircle2, ArrowUpCircle,
  ChevronDown, Video, VideoOff,
  RotateCcw, Power, AlertTriangle, Trash2, FileText,
  Megaphone, Gamepad2, Store, Plus, Usb, ShieldOff,
} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { isAdmin as isAdminRole } from '@/lib/roles';
import { toast } from 'sonner';
import {
  getDemoLocations, getAllDemoLocations, getStationCountsByLocation,
  createDemoLocation, updateDemoLocation,
  getDemoStations,
  sendCommand, sendBulkCommand,
  updateDemoStation, deleteDemoStation,
  formatLastSeen, isStationOnline,
  sendHudUpdate,
  getLatestRelease, getReleaseHistory, publishRelease, getStationCommands,
  setDemoMode, setAdminMode, setUsbUnlocked, ADMIN_MODE_MINUTES, USB_UNLOCK_MINUTES,
  isUsbUnlocked, formatRemaining,
  isAdminModeStale, formatAdminModeAge, canSendCommand,
} from '@/lib/api/demoStations';
import type { DemoStationWithDetails, DemoLocation, DemoCommandType, DemoStationCategory, AgentRelease } from '@/types/demo';
import { DEMO_STATUS_CONFIG, DEMO_COMMAND_LABELS, DEMO_CATEGORY_LABELS } from '@/types/demo';
import { DEMO_SETUP_STEPS_JSX } from '@/lib/demoSetup';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { PingButton } from '@/components/PingButton';

// ── Agent updates card ────────────────────────────────────────────────────────
interface AgentUpdatesCardProps {
  stations: DemoStationWithDetails[];
  currentRelease: AgentRelease | null;
  publishedBy: string;
  onPushAll: () => void;
  isPushingAll: boolean;
}

function AgentUpdatesCard({ stations, currentRelease, publishedBy, onPushAll, isPushingAll }: AgentUpdatesCardProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [version, setVersion] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');

  const upToDateCount = currentRelease
    ? stations.filter((s) => s.agent_version === currentRelease.version).length
    : 0;
  const outdatedCount = stations.length - upToDateCount;

  const publishMutation = useMutation({
    mutationFn: () => publishRelease({
      version: version.trim(),
      download_url: downloadUrl.trim(),
      release_notes: releaseNotes.trim() || undefined,
      published_by: publishedBy,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-latest-release'] });
      setShowForm(false);
      setVersion('');
      setDownloadUrl('');
      setReleaseNotes('');
      toast.success(`Agent v${version.trim()} published`);
    },
    onError: (err: Error) => toast.error('Publish failed: ' + err.message),
  });

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-blue-500/10 rounded-lg shrink-0">
            <ArrowUpCircle className="h-6 w-6 text-blue-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <h3 className="font-semibold text-sm">Agent Auto-Update</h3>
                {currentRelease ? (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Current published version:{' '}
                    <span className="font-mono font-medium text-foreground">v{currentRelease.version}</span>
                    {currentRelease.published_by && (
                      <span className="ml-1">· by {currentRelease.published_by}</span>
                    )}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-0.5">No version published yet</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {currentRelease && stations.length > 0 && (
                  <button
                    className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
                    onClick={onPushAll}
                    disabled={isPushingAll || outdatedCount === 0}
                    title={outdatedCount === 0 ? 'All stations are up to date' : `Push update to ${outdatedCount} outdated station${outdatedCount !== 1 ? 's' : ''}`}
                  >
                    <Upload className="h-3.5 w-3.5" />
                    {isPushingAll ? 'Pushing…' : outdatedCount > 0 ? `Push to ${outdatedCount} outdated` : 'All up to date'}
                  </button>
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowForm(!showForm)}>
                  Publish new version
                </Button>
              </div>
            </div>

            {/* Station version summary */}
            {currentRelease && stations.length > 0 && (
              <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                  {upToDateCount} up to date
                </span>
                {outdatedCount > 0 && (
                  <span className="flex items-center gap-1 text-yellow-600">
                    <ArrowUpCircle className="h-3.5 w-3.5" />
                    {outdatedCount} need update
                  </span>
                )}
              </div>
            )}

            {/* Publish form */}
            {showForm && (
              <div className="mt-4 space-y-3 border-t pt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Version <span className="text-red-500">*</span></label>
                    <Input
                      placeholder="e.g. 3.2.11"
                      value={version}
                      onChange={(e) => setVersion(e.target.value)}
                      className="h-8 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Release notes</label>
                    <Input
                      placeholder="Optional"
                      value={releaseNotes}
                      onChange={(e) => setReleaseNotes(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Download URL <span className="text-red-500">*</span></label>
                  <Input
                    placeholder="https://…/AOI Demo Agent Setup 3.2.11.exe"
                    value={downloadUrl}
                    onChange={(e) => setDownloadUrl(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    disabled={!version.trim() || !downloadUrl.trim() || publishMutation.isPending}
                    onClick={() => publishMutation.mutate()}
                  >
                    {publishMutation.isPending ? 'Publishing…' : 'Publish'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                  <p className="text-xs text-muted-foreground ml-auto">
                    Agents will download this on next startup or when pushed
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Logs modal ────────────────────────────────────────────────────────────────
function LogsModal({ stationId, stationName, onClose }: { stationId: string; stationName: string; onClose: () => void }) {
  const { data: commands } = useQuery({
    queryKey: ['demo-commands', stationId],
    queryFn: () => getStationCommands(stationId, 20),
    refetchInterval: 2000,
  });
  const logsCmd = commands?.find((c: any) => c.command_type === 'get_logs' && c.status === 'completed' && c.result);
  const res = logsCmd?.result as any;
  const lines: string[] = res?.lines ?? [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-sm">Agent Log — {stationName}</DialogTitle>
        </DialogHeader>
        {!logsCmd ? (
          <p className="text-xs text-muted-foreground py-4 text-center">Waiting for agent response…</p>
        ) : res?.error ? (
          <p className="text-xs text-red-500 py-2">Error reading log: {res.error}</p>
        ) : lines.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">Log file is empty.</p>
        ) : (
          <pre className="text-[10px] font-mono leading-relaxed whitespace-pre-wrap break-all p-3 bg-gray-950 text-gray-300 rounded-lg max-h-96 overflow-y-auto">
            {lines.join('\n')}
          </pre>
        )}
        <DialogFooter>
          <Button size="sm" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Station card ──────────────────────────────────────────────────────────────
interface StationCardProps {
  station: DemoStationWithDetails;
  latestVersion: string | null;
  latestRelease: AgentRelease | null;
  userName: string;
  onCommand: (stationId: string, cmd: DemoCommandType, payload?: Record<string, unknown>) => void;
  onEdit: (station: DemoStationWithDetails) => void;
  onToggleVideo: (stationId: string, enabled: boolean) => void;
  onHudPush: (stationId: string, buildId: string) => void;
  onHudToggle: (stationId: string, enabled: boolean) => void;
  onAdToggle: (stationId: string, enabled: boolean) => void;
  onDemoMode: (station: DemoStationWithDetails, enter: boolean) => void;
  onAdminMode: (station: DemoStationWithDetails, enter: boolean) => void;
  onEnableUsb: (stationId: string, enable: boolean) => void;
  onDelete: (station: DemoStationWithDetails) => void;
  /** False for commands the database would refuse this user; those are not shown. */
  canSend: (cmd: DemoCommandType) => boolean;
  isRetail?: boolean;
}

function StationCard({ station, latestVersion, latestRelease, userName, onCommand, onEdit, onToggleVideo, onHudPush, onHudToggle, onAdToggle, onDemoMode, onAdminMode, onEnableUsb, onDelete, canSend, isRetail = false }: StationCardProps) {
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);
  const online = isStationOnline(station);
  const effectiveStatus = online ? station.status : (station.status === 'retiring' ? 'retiring' : 'offline');
  const cfg = DEMO_STATUS_CONFIG[effectiveStatus];
  const hasAgentUpdate = !!(latestVersion && station.agent_version && station.agent_version !== latestVersion);
  // The two modes are independent: either can be lit without the other. The USB
  // button stays lit under Admin Mode regardless, since it holds USB open.
  const isInAdminMode = station.admin_mode === true;
  // Admin Mode holds USB open with no deadline of its own on a persistent
  // station, so usb_unlocked_until is null there. Reading only the timestamp
  // would show USB as blocked on a machine whose ports are wide open.
  const usbOn         = isInAdminMode || isUsbUnlocked(station.usb_unlocked_until);
  const usbCountdown  = isUsbUnlocked(station.usb_unlocked_until)
    ? `${formatRemaining(station.usb_unlocked_until)} left`
    : 'Admin Mode';
  const isInDemoMode  = station.demo_mode === true;
  // Nothing ends a persistent station's unlock but a person, so the fleet view
  // is the only place a forgotten one can surface. Shown over the preview
  // because that is the part of the card someone actually looks at.
  const adminStale = !isRetail && isAdminModeStale(station.admin_mode, station.admin_mode_since);
  const adminStaleWarning = adminStale && (
    <div
      className="absolute inset-x-1.5 top-1.5 z-10 flex items-start gap-1.5 rounded-md bg-red-600/90 px-2 py-1 text-[10px] font-medium text-white shadow-sm backdrop-blur-[2px]"
      title={station.admin_mode_persistent
        ? `This station is set never to relock, and has been fully unlocked (USB, Run, Control Panel, Task Manager) for ${formatAdminModeAge(station.admin_mode_since)}. Exit Admin Mode if the work is finished.`
        : `Admin Mode should have relocked after ${ADMIN_MODE_MINUTES / 60}h but has been open for ${formatAdminModeAge(station.admin_mode_since)} — usually a station that was powered off mid-unlock and has not booted since.`}
    >
      <AlertTriangle className="h-3 w-3 shrink-0 mt-px" />
      <span className="leading-tight">
        Admin Mode for {formatAdminModeAge(station.admin_mode_since)}
        {station.admin_mode_persistent ? ' — never relocks on its own' : ' — should have relocked'}
      </span>
    </div>
  );

  return (
    <Card
      className="relative cursor-pointer hover:shadow-md transition-shadow group"
      onClick={() => navigate(`/demo-stations/${station.id}`)}
    >
      {/* Status stripe — rounded to match card corners so overflow-hidden isn't needed */}
      <div className={cn('absolute inset-y-0 left-0 w-1 rounded-l-lg', {
        'bg-green-500':  effectiveStatus === 'online',
        'bg-gray-300':   effectiveStatus === 'offline',
        'bg-blue-500':   effectiveStatus === 'updating',
        'bg-red-500':    effectiveStatus === 'error',
        'bg-orange-500': effectiveStatus === 'retiring',
      })} />

      <CardHeader className="pl-5 pb-2 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            {/* Status line */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={cn('h-2 w-2 rounded-full shrink-0', cfg.dot,
                online && station.status === 'online' ? 'animate-pulse' : ''
              )} />
              <span className={cn('text-xs font-medium', cfg.color)}>{cfg.label}</span>
              {station.status === 'retiring' && station.retire_phase && (
                <span className="text-[10px] text-orange-500 italic">{station.retire_phase}</span>
              )}
              {!isRetail && station.pending_updates > 0 && (
                <Badge variant="outline" className="text-[10px] h-4 px-1 text-yellow-700 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20">
                  {station.pending_updates} update{station.pending_updates !== 1 ? 's' : ''}
                </Badge>
              )}
              {!isRetail && hasAgentUpdate && (
                <Badge variant="outline" className="text-[10px] h-4 px-1 text-blue-700 border-blue-400 bg-blue-50 dark:bg-blue-900/20">
                  agent update
                </Badge>
              )}
              {station.category === 'other' && (
                <Badge variant="outline" className="text-[10px] h-4 px-1 text-purple-700 border-purple-400 bg-purple-50 dark:bg-purple-900/20">
                  Other
                </Badge>
              )}
              {/* An unlock with no timer is only safe because someone chose it
                  per station — so say so on the card, not just in the dialog. */}
              {!isRetail && isInAdminMode && (
                <Badge variant="outline" className="text-[10px] h-4 px-1 text-red-700 border-red-400 bg-red-50 dark:bg-red-900/20">
                  {station.admin_mode_persistent ? 'Admin · no relock' : 'Admin'}
                </Badge>
              )}
            </div>

            {/* Station name */}
            <h3 className="font-semibold text-base truncate mt-1 leading-tight">{station.name}</h3>

            {/* Build description */}
            {station.build_sku ? (
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                <span className="font-mono text-primary">{station.build_sku}</span>
                {station.build_description && (
                  <span className="ml-1">— {station.build_description}</span>
                )}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground italic mt-0.5">No build assigned</p>
            )}
          </div>

          {/* Edit button — stops propagation so card click doesn't fire */}
          {!isRetail && (
            <Button
              size="icon" variant="ghost" className="h-7 w-7 shrink-0"
              onClick={(e) => { e.stopPropagation(); onEdit(station); }}
              title="Edit station"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="pl-5 pr-5 pb-4 space-y-3">
        {/* Screenshot — full 16:9 with object-contain so full screen is visible */}
        {station.screenshot_url ? (
          <div className="w-full aspect-video rounded overflow-hidden bg-black border border-gray-200 dark:border-gray-700 relative">
            <img
              key={station.screenshot_url}
              src={station.screenshot_url}
              alt="Last screenshot"
              className={cn("w-full h-full object-contain transition-all duration-500", !online && "grayscale")}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            {adminStaleWarning}
          </div>
        ) : (
          <div className="w-full aspect-video rounded bg-gray-100 dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-700 flex flex-col items-center justify-center gap-1 relative">
            <Camera className="h-6 w-6 text-muted-foreground/40" />
            <span className="text-[11px] text-muted-foreground">No screenshot yet</span>
            {adminStaleWarning}
          </div>
        )}

        {/* Timestamp + quick actions */}
        <div className="flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
          <span className="text-[11px] text-muted-foreground">
            {station.screenshot_at ? formatLastSeen(station.screenshot_at) : formatLastSeen(station.last_seen)}
          </span>
          <div className="flex items-center gap-0.5">
            {!isRetail && (
              <>
                <PingButton stationId={station.id} createdBy={userName} variant="icon" />
                <Button size="icon" variant="ghost" className="h-6 w-6"
                  onClick={() => onCommand(station.id, 'screenshot')} title="Take screenshot">
                  <Camera className="h-3 w-3" />
                </Button>
              </>
            )}
            <Button size="icon" variant="ghost"
              className={cn('h-6 w-6', station.video_enabled === false ? 'text-gray-400' : '')}
              onClick={() => onToggleVideo(station.id, !(station.video_enabled !== false))}
              title={station.video_enabled === false ? 'Turn video on' : 'Turn video off'}>
              {station.video_enabled === false
                ? <VideoOff className="h-3 w-3" />
                : <Video className="h-3 w-3" />}
            </Button>
            {/* More dropdown */}
            <div className="relative">
              <Button size="icon" variant="ghost" className="h-6 w-6"
                onClick={() => setShowMore(!showMore)} title="More actions">
                <ChevronDown className="h-3 w-3" />
              </Button>
              {showMore && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMore(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-900 border rounded-md shadow-lg py-1 min-w-[190px]">
                    <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-yellow-700"
                      onClick={() => { onCommand(station.id, 'reboot'); setShowMore(false); }}>
                      <RotateCcw className="h-3 w-3" />Reboot
                    </button>
                    {/* Retail staff get this one: the shop floor needs USB for a
                        customer's save files or a vendor stick, and it never
                        drops the kiosk shell. Admin Mode stays restricted. */}
                    <button className={cn('flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800', usbOn ? 'text-orange-600' : 'text-blue-600')}
                      onClick={() => { onEnableUsb(station.id, !usbOn); setShowMore(false); }}>
                      <Usb className="h-3 w-3" />
                      {usbOn ? `Block USB (${usbCountdown})` : 'Enable USB'}
                    </button>
                    {!isRetail && (
                      <>
                        <button className={cn('flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800', isInDemoMode ? 'text-orange-600' : 'text-green-700')}
                          onClick={() => { onDemoMode(station, !isInDemoMode); setShowMore(false); }}>
                          <Gamepad2 className="h-3 w-3" />{isInDemoMode ? 'Exit Demo Mode' : 'Demo Mode'}
                        </button>
                        {canSend('admin_mode') && (
                        <button className={cn('flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800', isInAdminMode ? 'text-orange-600' : 'text-red-700')}
                          onClick={() => { onAdminMode(station, !isInAdminMode); setShowMore(false); }}>
                          <ShieldOff className="h-3 w-3" />{isInAdminMode ? 'Exit Admin Mode' : 'Admin Mode'}
                        </button>
                        )}
                        <hr className="my-1 border-gray-100 dark:border-gray-800" />
                        {station.build_id && (
                          <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-purple-600"
                            onClick={() => { onHudPush(station.id, station.build_id!); setShowMore(false); }}>
                            <Monitor className="h-3 w-3" />Push HUD
                          </button>
                        )}
                      </>
                    )}
                    <button className={cn('flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800', station.hud_enabled ? 'text-orange-600' : 'text-green-600')}
                      onClick={() => { onHudToggle(station.id, !station.hud_enabled); setShowMore(false); }}>
                      <Monitor className="h-3 w-3" />{station.hud_enabled ? 'Disable HUD' : 'Enable HUD'}
                    </button>
                    <button className={cn('flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800', station.ad_enabled ? 'text-orange-600' : 'text-green-600')}
                      onClick={() => { onAdToggle(station.id, !station.ad_enabled); setShowMore(false); }}>
                      <Megaphone className="h-3 w-3" />{station.ad_enabled ? 'Disable Ad' : 'Enable Ad'}
                    </button>
                    {!isRetail && (
                      <>
                        <hr className="my-1 border-gray-100 dark:border-gray-800" />
                        {canSend('update_windows') && (
                        <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800"
                          onClick={() => { onCommand(station.id, 'update_windows'); setShowMore(false); }}>
                          <Shield className="h-3 w-3 text-blue-500" />Windows Update
                        </button>
                        )}
                        {hasAgentUpdate && latestRelease && canSend('update_agent') && (
                          <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-blue-600"
                            onClick={() => { onCommand(station.id, 'update_agent', { version: latestRelease.version, download_url: latestRelease.download_url }); setShowMore(false); }}>
                            <ArrowUpCircle className="h-3 w-3" />Update Agent
                          </button>
                        )}
                        <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-muted-foreground"
                          onClick={() => { onCommand(station.id, 'get_logs', { lines: 5000 }); setShowMore(false); }}>
                          <FileText className="h-3 w-3" />Get Logs
                        </button>
                      </>
                    )}
                    <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-orange-600"
                      onClick={() => { onCommand(station.id, 'shutdown'); setShowMore(false); }}>
                      <Power className="h-3 w-3" />Shutdown
                    </button>
                    {!isRetail && (
                      <>
                        <hr className="my-1 border-gray-100 dark:border-gray-800" />
                        {canSend('retire') && (
                        <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-red-600"
                          onClick={() => { onCommand(station.id, 'retire'); setShowMore(false); }}>
                          <AlertTriangle className="h-3 w-3" />Retire Station
                        </button>
                        )}
                        <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-red-600"
                          onClick={() => { onDelete(station); setShowMore(false); }}>
                          <Trash2 className="h-3 w-3" />Delete record
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

      </CardContent>
    </Card>
  );
}

// ── Edit station dialog ───────────────────────────────────────────────────────
interface EditDialogProps {
  station: DemoStationWithDetails | null;
  locations: DemoLocation[];
  builds: { id: string; sku: string; description: string; status: string }[];
  /** Admins only: an unlock that never expires is not a call for the shop floor. */
  canSetAdminPolicy?: boolean;
  onClose: () => void;
  onSave: (id: string, updates: Record<string, unknown>) => void;
}

function EditStationDialog({ station, locations, builds, canSetAdminPolicy = false, onClose, onSave }: EditDialogProps) {
  const [name, setName]               = useState(station?.name ?? '');
  const [locationId, setLocationId]   = useState(station?.location_id ?? '');
  const [buildId, setBuildId]         = useState(station?.build_id ?? '');
  const [serialNumber, setSerialNumber] = useState(station?.serial_number ?? '');
  const [notes, setNotes]             = useState(station?.notes ?? '');
  const [category, setCategory]       = useState<DemoStationCategory>(station?.category ?? 'gaming');
  const [adminPersistent, setAdminPersistent] = useState(station?.admin_mode_persistent ?? false);
  const [buildSearch, setBuildSearch] = useState('');

  useEffect(() => {
    if (station) {
      setName(station.name);
      setLocationId(station.location_id ?? '');
      setBuildId(station.build_id ?? '');
      setSerialNumber(station.serial_number ?? '');
      setNotes(station.notes ?? '');
      setCategory(station.category ?? 'gaming');
      setAdminPersistent(station.admin_mode_persistent ?? false);
      setBuildSearch('');
    }
  }, [station]);

  if (!station) return null;

  const filteredBuilds = buildSearch.trim()
    ? builds.filter((b) => {
        const q = buildSearch.toLowerCase();
        return (b.sku ?? '').toLowerCase().includes(q) || (b.description ?? '').toLowerCase().includes(q);
      })
    : builds;

  const selectedBuild = builds.find((b) => b.id === buildId);

  return (
    <Dialog open={!!station} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Station</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>Station name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Display PC 1" />
          </div>
          <div className="space-y-1">
            <Label>Location</Label>
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">— Unassigned —</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.name} ({loc.shop_code})</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <Label>Category</Label>
            <div className="flex gap-2">
              {(['gaming', 'other'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={cn(
                    'flex-1 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors',
                    category === c
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800',
                  )}
                >
                  {DEMO_CATEGORY_LABELS[c]}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Other stations don't play flagship playlists.
            </p>
          </div>

          <div className="space-y-1">
            <Label>Serial Number</Label>
            <Input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} placeholder="e.g. SN123456" />
          </div>

          {/* Build picker with search */}
          <div className="space-y-1">
            <Label>Build on display</Label>
            {selectedBuild && (
              <p className="text-xs text-muted-foreground mb-1">
                Current: <span className="font-mono text-primary">{selectedBuild.sku}</span>
                {' '}{selectedBuild.status !== 'Active' && (
                  <span className="text-yellow-600">({selectedBuild.status})</span>
                )}
              </p>
            )}
            <Input
              placeholder="Search by SKU or name..."
              value={buildSearch}
              onChange={(e) => setBuildSearch(e.target.value)}
              className="mb-1"
            />
            <select
              value={buildId}
              onChange={(e) => { setBuildId(e.target.value); setBuildSearch(''); }}
              size={Math.min(filteredBuilds.length + 1, 8)}
              className="w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            >
              <option value="">— None —</option>
              {filteredBuilds.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.sku}{b.status !== 'Active' ? ` (${b.status})` : ''} — {b.description}
                </option>
              ))}
            </select>
            {buildSearch && filteredBuilds.length === 0 && (
              <p className="text-xs text-muted-foreground">No builds match "{buildSearch}"</p>
            )}
          </div>

          {/* Station policy, not a live toggle: it decides how the next Admin
              Mode unlock behaves, and does not end one already running. */}
          {canSetAdminPolicy && (
            <div className="space-y-1">
              <Label>Admin Mode</Label>
              <div className="flex gap-2">
                {([false, true] as const).map((persistent) => (
                  <button
                    key={String(persistent)}
                    type="button"
                    onClick={() => setAdminPersistent(persistent)}
                    className={cn(
                      'flex-1 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors',
                      adminPersistent === persistent
                        ? persistent
                          ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-950/40'
                          : 'border-primary bg-primary/10 text-primary'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800',
                    )}
                  >
                    {persistent ? `Never relocks` : `Relocks after ${ADMIN_MODE_MINUTES / 60}h`}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">
                {adminPersistent
                  ? 'Admin Mode stays open on this station until someone exits it — across reboots, indefinitely. USB, Run, Control Panel and Task Manager stay available. Only for workshop or bench machines a customer cannot reach.'
                  : `Admin Mode lifts every restriction, then restores them ${ADMIN_MODE_MINUTES / 60}h later even if the station reboots in between.`}
              </p>
            </div>
          )}

          <div className="space-y-1">
            <Label>Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">Machine ID (read-only)</Label>
            <p className="font-mono text-xs bg-gray-50 dark:bg-gray-900 px-3 py-2 rounded border">{station.machine_id}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(station.id, {
            name,
            location_id:   locationId    || null,
            build_id:      buildId       || null,
            serial_number: serialNumber.trim() || null,
            notes:         notes         || null,
            category,
            // Only admins see the control; anyone else must not blank the flag.
            ...(canSetAdminPolicy ? { admin_mode_persistent: adminPersistent } : {}),
          })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Manage shops dialog ───────────────────────────────────────────────────────
// "Remove" deactivates rather than deletes: technicians.location_ids is a plain
// uuid[] with no FK, so a hard delete would silently strip retail users of their
// shop assignments and drop the location's wall_config with no way back.
function ManageShopsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const { data: allLocations = [], isLoading } = useQuery({
    queryKey: ['demo-locations-all'],
    queryFn: getAllDemoLocations,
    enabled: open,
  });
  const { data: stationCounts = {} } = useQuery({
    queryKey: ['demo-station-counts-by-location'],
    queryFn: getStationCountsByLocation,
    enabled: open,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['demo-locations-all'] });
    queryClient.invalidateQueries({ queryKey: ['demo-locations'] });
  };
  const describeError = (err: any) =>
    err?.code === '23505' ? 'That shop code is already in use' : (err?.message ?? 'Unknown error');

  const createMutation = useMutation({
    mutationFn: () => createDemoLocation({
      name: newName.trim(),
      shop_code: newCode.trim().toUpperCase(),
      address: newAddress.trim() || undefined,
      sort_order: allLocations.reduce((max, l) => Math.max(max, l.sort_order), 0) + 1,
    }),
    onSuccess: () => {
      toast.success(`Shop "${newName.trim()}" added`);
      setNewName(''); setNewCode(''); setNewAddress('');
      invalidate();
    },
    onError: (err: any) => toast.error(`Could not add shop: ${describeError(err)}`),
  });

  const renameMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      updateDemoLocation(id, { name: editName.trim(), shop_code: editCode.trim().toUpperCase() }),
    onSuccess: () => { toast.success('Shop updated'); setEditId(null); invalidate(); },
    onError: (err: any) => toast.error(`Could not update shop: ${describeError(err)}`),
  });

  const activeMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      updateDemoLocation(id, { is_active: active }),
    onSuccess: (_d, { active }) => {
      toast.success(active ? 'Shop restored' : 'Shop removed');
      setConfirmRemoveId(null);
      invalidate();
    },
    onError: (err: any) => toast.error(`Could not update shop: ${describeError(err)}`),
  });

  const active = allLocations.filter((l) => l.is_active);
  const inactive = allLocations.filter((l) => !l.is_active);
  const canAdd = !!newName.trim() && !!newCode.trim() && !createMutation.isPending;

  function startEdit(loc: DemoLocation) {
    setEditId(loc.id);
    setEditName(loc.name);
    setEditCode(loc.shop_code);
    setConfirmRemoveId(null);
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="h-4 w-4" />Manage Shops
          </DialogTitle>
        </DialogHeader>

        {/* -mx-1/px-1: overflow-y-auto also clips the x-axis, which cuts the
            focus ring off the left edge of the inputs. */}
        <div className="space-y-4 py-1 max-h-[60vh] overflow-y-auto px-1 -mx-1">
          {isLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading shops…</p>
          ) : (
            <div className="divide-y dark:divide-gray-800">
              {active.map((loc) => {
                const count = stationCounts[loc.id] ?? 0;
                const editing = editId === loc.id;
                const confirming = confirmRemoveId === loc.id;
                return (
                  <div key={loc.id} className="py-2.5">
                    {editing ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder="Shop name"
                          className="h-8 text-sm flex-1"
                        />
                        <Input
                          value={editCode}
                          onChange={(e) => setEditCode(e.target.value)}
                          placeholder="CODE"
                          className="h-8 text-sm w-24 font-mono uppercase"
                        />
                        <Button
                          size="sm" className="h-8"
                          disabled={!editName.trim() || !editCode.trim() || renameMutation.isPending}
                          onClick={() => renameMutation.mutate({ id: loc.id })}
                        >Save</Button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditId(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">
                            {loc.name}
                            <span className="ml-2 font-mono text-xs text-muted-foreground">{loc.shop_code}</span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {count} station{count !== 1 ? 's' : ''}
                          </p>
                        </div>
                        {confirming ? (
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className="text-xs text-muted-foreground">Remove?</span>
                            <Button
                              size="sm" variant="destructive" className="h-7 text-xs"
                              disabled={activeMutation.isPending}
                              onClick={() => activeMutation.mutate({ id: loc.id, active: false })}
                            >Remove</Button>
                            <Button
                              size="sm" variant="ghost" className="h-7 text-xs"
                              onClick={() => setConfirmRemoveId(null)}
                            >Cancel</Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-0.5 shrink-0">
                            <Button
                              size="icon" variant="ghost" className="h-7 w-7"
                              onClick={() => startEdit(loc)} title="Rename shop"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="icon" variant="ghost" className="h-7 w-7 text-red-600 disabled:text-muted-foreground"
                              disabled={count > 0}
                              onClick={() => { setConfirmRemoveId(loc.id); setEditId(null); }}
                              title={count > 0
                                ? `Reassign this shop's ${count} station${count !== 1 ? 's' : ''} before removing it`
                                : 'Remove shop'}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {active.length === 0 && (
                <p className="text-sm text-muted-foreground py-3">No active shops yet.</p>
              )}
            </div>
          )}

          {/* Removed shops — restorable */}
          {inactive.length > 0 && (
            <div className="border-t pt-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Removed
              </p>
              <div className="divide-y dark:divide-gray-800">
                {inactive.map((loc) => (
                  <div key={loc.id} className="flex items-center gap-2 py-2">
                    <p className="text-sm text-muted-foreground min-w-0 flex-1 truncate">
                      {loc.name}
                      <span className="ml-2 font-mono text-xs">{loc.shop_code}</span>
                    </p>
                    <Button
                      size="sm" variant="outline" className="h-7 text-xs shrink-0"
                      disabled={activeMutation.isPending}
                      onClick={() => activeMutation.mutate({ id: loc.id, active: true })}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />Restore
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add a shop */}
          <div className="border-t pt-3 space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Add a shop</p>
            <div className="flex items-center gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Shop name"
                className="h-8 text-sm flex-1"
              />
              <Input
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                placeholder="CODE"
                className="h-8 text-sm w-24 font-mono uppercase"
              />
            </div>
            <Input
              value={newAddress}
              onChange={(e) => setNewAddress(e.target.value)}
              placeholder="Address (optional)"
              className="h-8 text-sm"
            />
            <Button
              size="sm" className="h-8"
              disabled={!canAdd}
              onClick={() => createMutation.mutate()}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              {createMutation.isPending ? 'Adding…' : 'Add shop'}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Confirm dialog ────────────────────────────────────────────────────────────
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({ open, title, description, confirmLabel = 'Confirm', destructive, onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{description}</p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button variant={destructive ? 'destructive' : 'default'} onClick={onConfirm}>{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Video control card ────────────────────────────────────────────────────────
interface VideoControlCardProps {
  stations: DemoStationWithDetails[];
  locations: DemoLocation[];
  onToggle: (stationIds: string[], enabled: boolean) => void;
}

function VideoControlCard({ stations, locations, onToggle }: VideoControlCardProps) {
  const videoOn = (s: DemoStationWithDetails) => s.video_enabled !== false;

  // Group by location (only locations that have stations)
  const byLocation = locations
    .map((loc) => ({ ...loc, stns: stations.filter((s) => s.location_id === loc.id) }))
    .filter((g) => g.stns.length > 0);

  // Group by build model
  const buildMap = new Map<string, { sku: string; description: string | null; stns: DemoStationWithDetails[] }>();
  stations.forEach((s) => {
    if (!s.build_id || !s.build_sku) return;
    if (!buildMap.has(s.build_id)) buildMap.set(s.build_id, { sku: s.build_sku, description: s.build_description, stns: [] });
    buildMap.get(s.build_id)!.stns.push(s);
  });
  const byBuild = Array.from(buildMap.values()).sort((a, b) => a.sku.localeCompare(b.sku));

  function ToggleRow({ label, sub, stns }: { label: string; sub: string; stns: DemoStationWithDetails[] }) {
    const allOn  = stns.every(videoOn);
    const allOff = stns.every((s) => !videoOn(s));
    const mixed  = !allOn && !allOff;
    return (
      <div className="flex items-center justify-between py-2 gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{label}</p>
          <p className="text-xs text-muted-foreground">{sub}{mixed ? ` · ${stns.filter(videoOn).length}/${stns.length} on` : ''}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {mixed && (
            <button
              onClick={() => onToggle(stns.map((s) => s.id), false)}
              className="text-[11px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
            >All off</button>
          )}
          <button
            onClick={() => onToggle(stns.map((s) => s.id), !allOn)}
            className={cn(
              'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none',
              allOn ? 'bg-green-500' : mixed ? 'bg-yellow-400' : 'bg-gray-300 dark:bg-gray-600',
            )}
            title={allOn ? 'Turn off' : 'Turn on'}
          >
            <span className={cn(
              'pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform',
              allOn ? 'translate-x-[18px]' : mixed ? 'translate-x-[9px]' : 'translate-x-0.5',
            )} />
          </button>
        </div>
      </div>
    );
  }

  if (stations.length === 0) return null;

  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-1">
          <Video className="h-4 w-4" /> Video Playback
        </h3>
        <p className="text-xs text-muted-foreground mb-4">Toggle video playback on stations. Green = on · Yellow = mixed · Gray = off.</p>

        <div className="grid sm:grid-cols-2 gap-x-8">
          {/* By Shop */}
          {byLocation.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">By Shop</p>
              <div className="divide-y dark:divide-gray-800">
                {byLocation.map((loc) => (
                  <ToggleRow
                    key={loc.id}
                    label={loc.name}
                    sub={`${loc.stns.length} station${loc.stns.length !== 1 ? 's' : ''}`}
                    stns={loc.stns}
                  />
                ))}
              </div>
            </div>
          )}

          {/* By Build Model */}
          {byBuild.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">By Build Model</p>
              <div className="divide-y dark:divide-gray-800">
                {byBuild.map((b) => (
                  <ToggleRow
                    key={b.sku}
                    label={b.sku}
                    sub={`${b.description ? b.description + ' · ' : ''}${b.stns.length} station${b.stns.length !== 1 ? 's' : ''}`}
                    stns={b.stns}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Agent download card ───────────────────────────────────────────────────────
function AgentDownloadCard({ release, history = [] }: { release: AgentRelease | null | undefined; history?: AgentRelease[] }) {
  const [showGuide, setShowGuide] = useState(false);

  async function downloadSetupBat() {
    const ps1Url = window.location.origin + '/AOI-Demo-Setup.ps1';
    const bat = [
      '@echo off',
      'echo Downloading AOI Demo Setup script...',
      `powershell -NoProfile -ExecutionPolicy Bypass -Command "Invoke-WebRequest -Uri '${ps1Url}' -OutFile '%TEMP%\\AOI-Demo-Setup.ps1'"`,
      'echo Running setup...',
      'powershell -ExecutionPolicy Bypass -File "%TEMP%\\AOI-Demo-Setup.ps1"',
    ].join('\r\n');
    const zip = new JSZip();
    zip.file('AOI-Demo-Setup.bat', bat);
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'AOI-Demo-Setup.zip';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Download className="h-4 w-4" /> Downloads
        </h3>

        <div className="divide-y dark:divide-gray-800">
          {/* Setup script — single row with .bat / .ps1 dropdown */}
          <div className="flex items-center gap-4 py-3 first:pt-0">
            <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg shrink-0">
              <Monitor className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">AOI Demo Setup</p>
              <button
                onClick={() => setShowGuide((v) => !v)}
                className="mt-0.5 flex items-center gap-0.5 text-[11px] text-primary hover:underline"
              >
                <ChevronDown className={cn('h-3 w-3 transition-transform', showGuide && 'rotate-180')} />
                {showGuide ? 'Hide guide' : 'Setup guide'}
              </button>
            </div>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button size="sm" className="shrink-0">
                  <Download className="h-3.5 w-3.5 mr-1.5" />Download<ChevronDown className="h-3 w-3 ml-1 opacity-70" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={4}
                  className="z-50 min-w-[190px] rounded-md border bg-popover p-1 shadow-md"
                >
                  <DropdownMenu.Item
                    className="flex flex-col cursor-pointer rounded-sm px-3 py-2 text-sm outline-none hover:bg-accent"
                    onSelect={downloadSetupBat}
                  >
                    <span className="font-medium">AOI-Demo-Setup.zip</span>
                    <span className="text-xs text-muted-foreground mt-0.5">Extract, then double-click the .bat</span>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item asChild>
                    <a
                      href="/AOI-Demo-Setup.ps1"
                      download="AOI-Demo-Setup.ps1"
                      className="flex flex-col cursor-pointer rounded-sm px-3 py-2 text-sm outline-none hover:bg-accent"
                    >
                      <span className="font-medium">AOI-Demo-Setup.ps1</span>
                      <span className="text-xs text-muted-foreground mt-0.5">Right-click → Run as Administrator</span>
                    </a>
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>

          {/* Collapsible setup guide */}
          {showGuide && (
            <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-3 space-y-1.5">
              <p className="text-xs font-semibold text-blue-800 dark:text-blue-300">Demo PC setup — full walkthrough</p>
              <ol className="text-xs text-blue-700 dark:text-blue-400 space-y-1 list-decimal list-inside">
                {DEMO_SETUP_STEPS_JSX.map((step, i) => <li key={i}>{step}</li>)}
              </ol>
            </div>
          )}

          {/* Agent installer */}
          <div className="flex items-center gap-4 py-3 last:pb-0">
            <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg shrink-0">
              <Upload className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                {release ? `AOI Demo Agent v${release.version}` : 'AOI Demo Agent'}
              </p>
              <p className="text-xs text-muted-foreground">Install as Administrator after the setup script — one-time UAC, updates are silent after this</p>
            </div>
            {history.length > 1 ? (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button size="sm" className="shrink-0" disabled={!release}>
                    <Download className="h-3.5 w-3.5 mr-1.5" />Download<ChevronDown className="h-3 w-3 ml-1 opacity-70" />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    align="end"
                    sideOffset={4}
                    className="z-50 min-w-[230px] rounded-md border bg-popover p-1 shadow-md"
                  >
                    {history.map((r, i) => (
                      <DropdownMenu.Item key={r.id} asChild>
                        <a
                          href={r.download_url}
                          download={`AOI-Demo-Agent-Setup-${r.version}.zip`}
                          className="flex items-center justify-between cursor-pointer rounded-sm px-3 py-2 text-sm outline-none hover:bg-accent"
                        >
                          <span className="font-medium font-mono">v{r.version}</span>
                          {i === 0 && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 ml-2">current</span>
                          )}
                        </a>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            ) : (
              <Button asChild size="sm" className="shrink-0" disabled={!release}>
                <a
                  href={release?.download_url ?? '#'}
                  download={release ? `AOI-Demo-Agent-Setup-${release.version}.zip` : undefined}
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" />Download
                </a>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
/**
 * How long a burst of Realtime events is collapsed into a single refetch.
 *
 * Stations get the longer window: their events are heartbeats and screenshot
 * stamps, arriving at a rate set by fleet size rather than by anything the
 * viewer did, and the grid reads staleness from a five-minute `last_seen`
 * horizon anyway - so nothing visible can tell 5s from instant.
 *
 * Commands get a short one because that traffic *is* a response to a click:
 * someone presses Reboot and watches the row for an acknowledgement.
 */
const STATION_COALESCE_MS = 5_000;
const COMMAND_COALESCE_MS = 1_500;

export function DemoStationsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationFilter = searchParams.get('loc') ?? 'all';
  const categoryParam = searchParams.get('cat');
  const setLocationFilter = (loc: string) => {
    const next = new URLSearchParams(searchParams);
    if (loc === 'all') next.delete('loc'); else next.set('loc', loc);
    setSearchParams(next, { replace: true });
  };
  const setCategoryFilter = (cat: 'all' | DemoStationCategory) => {
    const next = new URLSearchParams(searchParams);
    if (cat === 'all') next.delete('cat'); else next.set('cat', cat);
    setSearchParams(next, { replace: true });
  };
  type StatusFilter = 'all' | 'online' | 'offline' | 'updates';
  const statusParam = searchParams.get('status');
  const statusFilter: StatusFilter =
    statusParam === 'online' || statusParam === 'offline' || statusParam === 'updates' ? statusParam : 'all';
  const setStatusFilter = (status: StatusFilter) => {
    const next = new URLSearchParams(searchParams);
    // Clicking the already-active tile clears the filter back to "all".
    const value = statusFilter === status ? 'all' : status;
    if (value === 'all') next.delete('status'); else next.set('status', value);
    setSearchParams(next, { replace: true });
  };
  const [editStation, setEditStation] = useState<DemoStationWithDetails | null>(null);
  const [pendingCommand, setPendingCommand] = useState<{ stationId: string; cmd: DemoCommandType } | null>(null);
  const [logsModal, setLogsModal] = useState<{ stationId: string; stationName: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DemoStationWithDetails | null>(null);
  const [manageShops, setManageShops] = useState(false);

  // Role — staleTime:0 so location_ids are always fresh (shops can be assigned after first login).
  // Throws if no session to trigger React Query retry rather than caching role:null.
  const { data: userData } = useCurrentUser();
  const isAdmin = isAdminRole(userData?.role);
  const isMarketing = userData?.role === 'marketing';
  const isFullAccess = isAdmin || isMarketing;
  const isRetail = userData?.role === 'retail';
  const isRetailShop = userData?.role === 'retail_shop';
  const isLimitedView = isRetail || isRetailShop;
  const canSend = (cmd: DemoCommandType) => canSendCommand(userData?.role, cmd);
  const retailLocationIds = userData?.locationIds ?? [];

  // Effective category: retail sees Gaming, marketing sees Other,
  // admins can switch (URL param `cat`). For admins, default is 'all'.
  const lockedCategory: DemoStationCategory | null =
    isRetail || isRetailShop ? 'gaming'
    : isMarketing ? 'other'
    : null;
  const categoryFilter: 'all' | DemoStationCategory =
    lockedCategory ?? ((categoryParam === 'gaming' || categoryParam === 'other') ? categoryParam : 'all');
  const apiCategory: DemoStationCategory | undefined =
    categoryFilter === 'all' ? undefined : categoryFilter;

  // Current user name for command attribution — sourced from App.tsx's cached query
  const userName = userData?.name ?? '';

  const { data: locations = [] } = useQuery({
    queryKey: ['demo-locations'],
    queryFn: getDemoLocations,
  });

  const { data: stations = [], isLoading } = useQuery({
    queryKey: ['demo-stations', isLimitedView ? 'all' : locationFilter, apiCategory ?? 'all'],
    queryFn: () => getDemoStations(
      isLimitedView ? undefined : (locationFilter === 'all' ? undefined : locationFilter),
      apiCategory,
    ),
    // No refetchInterval. The Realtime subscription below already invalidates
    // this on every demo_stations change, and stations write on a 30s
    // heartbeat - so a 30s poll added a second full refetch of the whole list
    // for no information the subscription had not already delivered.
  });

  const { data: latestRelease } = useQuery({
    queryKey: ['agent-latest-release'],
    queryFn: getLatestRelease,
    staleTime: 60_000,
  });

  const { data: releaseHistory = [] } = useQuery({
    queryKey: ['agent-release-history'],
    queryFn: () => getReleaseHistory(5),
    staleTime: 60_000,
  });

  const { data: builds = [] } = useQuery({
    queryKey: ['builds-all-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('builds')
        .select('id, sku, description, status')
        .not('status', 'eq', 'archived')
        .order('sku');
      if (error) throw error;
      return data ?? [];
    },
  });

  // Realtime — refresh station list when any station updates.
  //
  // Coalesced, because the event rate is set by the fleet rather than by
  // anything the viewer did. Every station UPDATEs demo_stations on its 30s
  // heartbeat and again on its 60s screenshot, and each of those used to
  // trigger a refetch of *every* station - so the traffic from this page grew
  // with the square of the fleet size, and a quiet room full of idle stations
  // was its own worst case.
  //
  // A short window collapses a burst into one refetch. Stations are drawn from
  // a `last_seen` freshness test with a five-minute horizon (see
  // isStationOnline), so nothing on screen can tell the difference.
  useEffect(() => {
    const timers: Record<string, ReturnType<typeof setTimeout> | undefined> = {};

    const invalidateSoon = (key: string, withinMs: number) => {
      if (timers[key]) return;          // a refetch is already pending
      timers[key] = setTimeout(() => {
        timers[key] = undefined;
        queryClient.invalidateQueries({ queryKey: [key] });
      }, withinMs);
    };

    const channel = supabase
      .channel('demo-stations-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'demo_stations' }, () => {
        invalidateSoon('demo-stations', STATION_COALESCE_MS);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'demo_commands' }, () => {
        invalidateSoon('demo-commands', COMMAND_COALESCE_MS);
      })
      .subscribe();

    return () => {
      Object.values(timers).forEach(t => t && clearTimeout(t));
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // A shop can be removed while its tab is the active filter — fall back to All
  // so the grid doesn't sit empty against a location that no longer exists.
  useEffect(() => {
    if (locationFilter !== 'all' && locations.length > 0 && !locations.some((l) => l.id === locationFilter)) {
      setLocationFilter('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, locationFilter]);

  // Mutations
  const cmdMutation = useMutation({
    mutationFn: ({ stationId, cmd, payload }: { stationId: string; cmd: DemoCommandType; payload?: Record<string, unknown> }) =>
      sendCommand(stationId, cmd, payload ?? {}, userName),
    onSuccess: (_data, { cmd }) => {
      toast.success(`${DEMO_COMMAND_LABELS[cmd]} command sent`);
      queryClient.invalidateQueries({ queryKey: ['demo-commands'] });
    },
    onError: (err: Error) => toast.error(`Command failed: ${err.message}`),
  });

  const hudMutation = useMutation({
    mutationFn: ({ stationId, buildId }: { stationId: string; buildId: string }) =>
      sendHudUpdate(stationId, buildId, userName),
    onSuccess: () => {
      toast.success('HUD update sent');
      queryClient.invalidateQueries({ queryKey: ['demo-commands'] });
    },
    onError: (err: Error) => toast.error(`HUD update failed: ${err.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Record<string, unknown>; prevBuildId?: string | null }) =>
      updateDemoStation(id, updates as any),
    onSuccess: (_data, { id, updates, prevBuildId }) => {
      toast.success('Station updated');
      queryClient.invalidateQueries({ queryKey: ['demo-stations'] });
      setEditStation(null);
      // Auto-push HUD when build assignment changes
      if (updates.build_id && updates.build_id !== prevBuildId) {
        hudMutation.mutate({ stationId: id, buildId: updates.build_id as string });
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDemoStation(id),
    onSuccess: () => {
      toast.success('Station removed');
      queryClient.invalidateQueries({ queryKey: ['demo-stations'] });
      setDeleteTarget(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const bulkCmdMutation = useMutation({
    mutationFn: (cmd: DemoCommandType) =>
      sendBulkCommand(
        filteredStations.map((s) => s.id),
        cmd, {}, userName
      ),
    onSuccess: (_data, cmd) => toast.success(`${DEMO_COMMAND_LABELS[cmd]} sent to all visible stations`),
    onError: (err: Error) => toast.error(err.message),
  });

  const updateAgentMutation = useMutation({
    mutationFn: ({ stationIds, release }: { stationIds: string[]; release: AgentRelease }) =>
      sendBulkCommand(stationIds, 'update_agent', {
        version:      release.version,
        download_url: release.download_url,
      }, userName),
    onSuccess: (_data, { stationIds }) =>
      toast.success(`Agent update pushed to ${stationIds.length} station${stationIds.length !== 1 ? 's' : ''}`),
    onError: (err: Error) => toast.error('Update push failed: ' + err.message),
  });

  const hudToggleMutation = useMutation({
    mutationFn: async ({ stationId, enabled }: { stationId: string; enabled: boolean }) => {
      const { error } = await supabase.from('demo_stations').update({ hud_enabled: enabled }).eq('id', stationId);
      if (error) throw error;
      await sendCommand(stationId, 'toggle_hud', { enabled }, userName);
    },
    onSuccess: (_d, { enabled }) => {
      toast.success(enabled ? 'HUD enabled' : 'HUD disabled');
      queryClient.invalidateQueries({ queryKey: ['demo-stations'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const adToggleMutation = useMutation({
    mutationFn: async ({ stationId, enabled }: { stationId: string; enabled: boolean }) => {
      const { error } = await supabase.from('demo_stations').update({ ad_enabled: enabled }).eq('id', stationId);
      if (error) throw error;
      await sendCommand(stationId, 'toggle_ad', { enabled }, userName);
    },
    onSuccess: (_d, { enabled }) => {
      toast.success(enabled ? 'Ad enabled' : 'Ad disabled');
      queryClient.invalidateQueries({ queryKey: ['demo-stations'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Demo Mode: unlock kiosk + disable HUD/video/ad in one click (mirrors DemoStationDetailPage).
  const demoModeMutation = useMutation({
    mutationFn: ({ station, enter }: { station: DemoStationWithDetails; enter: boolean }) =>
      setDemoMode(station, enter, userName),
    onSuccess: (_d, { enter }) => {
      toast.success(enter ? 'Demo mode — kiosk unlocked, overlays hidden' : 'Kiosk mode restored');
      queryClient.invalidateQueries({ queryKey: ['demo-stations'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Admin Mode: lift every restriction (policies, USB, Explorer shell) so vendor
  // installers can run. Auto-relocks agent-side after ADMIN_MODE_MINUTES, unless
  // the station is flagged admin_mode_persistent, where it stays open.
  const adminModeMutation = useMutation({
    mutationFn: ({ station, enter }: { station: DemoStationWithDetails; enter: boolean }) =>
      setAdminMode(station, enter, userName),
    onSuccess: (_d, { station, enter }) => {
      toast.success(enter
        ? (station.admin_mode_persistent
            ? 'Admin mode — all restrictions lifted, stays open until you exit'
            : `Admin mode — all restrictions lifted, relocks in ${ADMIN_MODE_MINUTES / 60}h`)
        : 'Admin mode ended — restrictions restored');
      queryClient.invalidateQueries({ queryKey: ['demo-stations'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const usbUnlockMutation = useMutation({
    mutationFn: ({ stationId, enable }: { stationId: string; enable: boolean }) =>
      setUsbUnlocked(stationId, enable, userName),
    onSuccess: (_d, { enable }) => {
      toast.success(enable
        ? `USB storage enabled for ${USB_UNLOCK_MINUTES} min`
        : 'USB storage blocked');
      queryClient.invalidateQueries({ queryKey: ['demo-stations'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const videoToggleMutation = useMutation({
    mutationFn: async ({ stationIds, enabled }: { stationIds: string[]; enabled: boolean }) => {
      await Promise.all(stationIds.map((id) => updateDemoStation(id, { video_enabled: enabled } as any)));
      if (stationIds.length === 1) {
        await sendCommand(stationIds[0], 'toggle_video', { enabled }, userName);
      } else if (stationIds.length > 1) {
        await sendBulkCommand(stationIds, 'toggle_video', { enabled }, userName);
      }
    },
    onSuccess: (_data, { enabled, stationIds }) => {
      toast.success(`Video ${enabled ? 'enabled' : 'disabled'} on ${stationIds.length} station${stationIds.length !== 1 ? 's' : ''}`);
      queryClient.invalidateQueries({ queryKey: ['demo-stations'] });
    },
    onError: (err: Error) => toast.error('Video toggle failed: ' + err.message),
  });

  // Location/category-scoped station list — summary counts are always based on
  // this so the four stat tiles stay stable reference numbers regardless of
  // which one is currently active as a filter.
  const locationFilteredStations = locationFilter === 'all'
    ? stations
    : stations.filter((s) => s.location_id === locationFilter);

  // Summary counts
  const totalCount   = locationFilteredStations.length;
  const onlineCount  = locationFilteredStations.filter((s) => isStationOnline(s)).length;
  const offlineCount = totalCount - onlineCount;
  const updatesCount = locationFilteredStations.reduce((sum, s) => sum + (s.pending_updates ?? 0), 0);

  // Further narrowed by the clicked stat tile — this is what's actually shown/acted on.
  const filteredStations = statusFilter === 'all'
    ? locationFilteredStations
    : locationFilteredStations.filter((s) => {
        if (statusFilter === 'online') return isStationOnline(s);
        if (statusFilter === 'offline') return !isStationOnline(s);
        return (s.pending_updates ?? 0) > 0;
      });

  // Destructive command needs confirmation
  const NEEDS_CONFIRM: DemoCommandType[] = ['reboot', 'shutdown', 'retire'];

  function handleCommand(stationId: string, cmd: DemoCommandType, payload?: Record<string, unknown>) {
    if (NEEDS_CONFIRM.includes(cmd)) {
      setPendingCommand({ stationId, cmd });
    } else {
      cmdMutation.mutate({ stationId, cmd, payload });
      if (cmd === 'get_logs') {
        const station = stations.find(s => s.id === stationId);
        setLogsModal({ stationId, stationName: station?.name ?? stationId });
      }
    }
  }

  function confirmCommand() {
    if (!pendingCommand) return;
    cmdMutation.mutate(pendingCommand);
    setPendingCommand(null);
  }

  const CONFIRM_CONFIG: Partial<Record<DemoCommandType, { title: string; description: string; label: string }>> = {
    reboot:  { title: 'Reboot station?', description: 'The PC will restart in 30 seconds. Active VLC sessions will be interrupted.', label: 'Reboot' },
    shutdown: { title: 'Shut down station?', description: 'The PC will power off in 30 seconds.', label: 'Shutdown' },
    retire:  { title: 'Retire this station?', description: 'This will wipe the demo account and prepare the PC for collection. This cannot be undone.', label: 'Retire' },
  };
  const pendingCfg = pendingCommand ? CONFIRM_CONFIG[pendingCommand.cmd] ?? null : null;

  // Wait for App.tsx to populate the user role cache before rendering role-specific views
  if (!userData || userData.role === null) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Retail / Retail Shop view — stations in their assigned shops with limited commands
  if (isLimitedView) {
    const retailStations = stations.filter((s) => retailLocationIds.includes(s.location_id ?? ''));
    const retailLocations = locations.filter((l) => retailLocationIds.includes(l.id));
    const retailFiltered = locationFilter === 'all'
      ? retailStations
      : retailStations.filter((s) => s.location_id === locationFilter);
    const retailOnline = retailFiltered.filter((s) => isStationOnline(s)).length;

    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Monitor className="h-6 w-6" />Demo Stations
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Display PCs in your shop{retailLocations.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Location tabs — only assigned shops */}
        {retailLocations.length > 1 && (
          <div className="flex gap-1 overflow-x-auto pb-1">
            {[{ id: 'all', name: 'All Shops' }, ...retailLocations].map((loc) => (
              <button
                key={loc.id}
                onClick={() => setLocationFilter(loc.id === 'all' ? 'all' : loc.id)}
                className={cn(
                  'px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
                  (loc.id === 'all' ? locationFilter === 'all' : locationFilter === loc.id)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                )}
              >
                {loc.name}
              </button>
            ))}
          </div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-2 gap-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Total stations</p>
              <p className="text-2xl font-bold mt-1">{retailFiltered.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-green-500 inline-block" />Online
              </p>
              <p className="text-2xl font-bold mt-1 text-green-600">{retailOnline}</p>
            </CardContent>
          </Card>
        </div>

        {/* Station grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="h-56 animate-pulse bg-gray-100 dark:bg-gray-800" />
            ))}
          </div>
        ) : retailFiltered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Monitor className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg mb-1">No stations found</h3>
              <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                No display PCs are assigned to your shop yet. Contact an admin to set them up.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {retailFiltered.map((station) => (
              <StationCard
                key={station.id}
                station={station}
                latestVersion={latestRelease?.version ?? null}
                latestRelease={latestRelease ?? null}
                userName={userName}
                onCommand={handleCommand}
                onEdit={setEditStation}
                onToggleVideo={(id, enabled) => videoToggleMutation.mutate({ stationIds: [id], enabled })}
                onHudPush={(id, buildId) => hudMutation.mutate({ stationId: id, buildId })}
                onHudToggle={(id, enabled) => hudToggleMutation.mutate({ stationId: id, enabled })}
                onAdToggle={(id, enabled) => adToggleMutation.mutate({ stationId: id, enabled })}
                onDemoMode={(s, enter) => demoModeMutation.mutate({ station: s, enter })}
                onAdminMode={(s, enter) => adminModeMutation.mutate({ station: s, enter })}
                onEnableUsb={(sid, enable) => usbUnlockMutation.mutate({ stationId: sid, enable })}
                onDelete={setDeleteTarget}
                canSend={canSend}
                isRetail
              />
            ))}
          </div>
        )}

        {/* Video control */}
        <VideoControlCard
          stations={retailStations}
          locations={retailLocations}
          onToggle={(stationIds, enabled) => videoToggleMutation.mutate({ stationIds, enabled })}
        />

        {/* Confirm reboot/shutdown */}
        {pendingCommand && pendingCfg && (
          <ConfirmDialog
            open
            title={pendingCfg.title}
            description={pendingCfg.description}
            confirmLabel={pendingCfg.label}
            destructive={pendingCommand.cmd === 'retire'}
            onConfirm={confirmCommand}
            onCancel={() => setPendingCommand(null)}
          />
        )}
      </div>
    );
  }

  // Technician restricted view — download only
  if (!isFullAccess) {
    return (
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Monitor className="h-6 w-6" />Demo Stations
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Download the agent installer to set up a new display PC
          </p>
        </div>
        <AgentDownloadCard release={latestRelease} history={releaseHistory} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Monitor className="h-6 w-6" />Demo Stations
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isMarketing
              ? 'Manage non-gaming demo stations across all locations'
              : 'Manage in-store display PCs across all locations'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['demo-stations'] })}
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />Refresh
          </Button>
          {filteredStations.length > 0 && (
            <>
              <Button
                variant="outline" size="sm"
                onClick={() => bulkCmdMutation.mutate('ping')}
                disabled={bulkCmdMutation.isPending}
              >
                <Layers className="h-4 w-4 mr-1.5" />Ping All
              </Button>
              {updatesCount > 0 && canSend('update_windows') && (
                <Button
                  variant="outline" size="sm"
                  onClick={() => { if (confirm(`Force Windows Update on ${filteredStations.length} station(s)? This will scan, download, and install all pending updates.`)) bulkCmdMutation.mutate('update_windows'); }}
                  disabled={bulkCmdMutation.isPending}
                >
                  <Shield className="h-4 w-4 mr-1.5 text-blue-500" />Update All ({updatesCount})
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Location tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <div className="flex gap-1">
          {[{ id: 'all', name: 'All Locations', shop_code: '' }, ...locations].map((loc) => (
            <button
              key={loc.id}
              onClick={() => setLocationFilter(loc.id === 'all' ? 'all' : loc.id)}
              className={cn(
                'px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
                (loc.id === 'all' ? locationFilter === 'all' : locationFilter === loc.id)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              )}
            >
              {loc.name}
            </button>
          ))}
        </div>
        {locationFilter !== 'all' && categoryFilter !== 'other' && (
          <Button
            variant="outline" size="sm"
            onClick={() => navigate(`/demo-stations/wall-planner/${locationFilter}`)}
          >
            <Layers className="h-4 w-4 mr-1.5" />Wall Planner
          </Button>
        )}
        {isAdmin && (
          <Button variant="outline" size="sm" className="shrink-0" onClick={() => setManageShops(true)}>
            <Store className="h-4 w-4 mr-1.5" />Manage Shops
          </Button>
        )}
      </div>

      {/* Category sub-filter — admin only; retail/marketing have a locked category */}
      {isAdmin && (
        <div className="flex items-center gap-2 -mt-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Category</span>
          <div className="flex gap-1">
            {(['all', 'gaming', 'other'] as const).map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-medium transition-colors',
                  categoryFilter === cat
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700',
                )}
              >
                {cat === 'all' ? 'All' : DEMO_CATEGORY_LABELS[cat]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Summary stats — click a tile to filter the list below; click again to clear */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card
          role="button"
          onClick={() => setStatusFilter('all')}
          className={cn('cursor-pointer transition-colors hover:border-primary/50', statusFilter === 'all' && 'ring-2 ring-primary border-primary')}
        >
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total stations</p>
            <p className="text-2xl font-bold mt-1">{totalCount}</p>
          </CardContent>
        </Card>
        <Card
          role="button"
          onClick={() => setStatusFilter('online')}
          className={cn('cursor-pointer transition-colors hover:border-primary/50', statusFilter === 'online' && 'ring-2 ring-primary border-primary')}
        >
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-green-500 inline-block" />Online
            </p>
            <p className="text-2xl font-bold mt-1 text-green-600">{onlineCount}</p>
          </CardContent>
        </Card>
        <Card
          role="button"
          onClick={() => setStatusFilter('offline')}
          className={cn('cursor-pointer transition-colors hover:border-primary/50', statusFilter === 'offline' && 'ring-2 ring-primary border-primary')}
        >
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <WifiOff className="h-3 w-3" />Offline
            </p>
            <p className="text-2xl font-bold mt-1 text-gray-400">{offlineCount}</p>
          </CardContent>
        </Card>
        <Card
          role="button"
          onClick={() => setStatusFilter('updates')}
          className={cn('cursor-pointer transition-colors hover:border-primary/50', statusFilter === 'updates' && 'ring-2 ring-primary border-primary')}
        >
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Shield className="h-3 w-3 text-yellow-500" />Pending Windows updates
            </p>
            <p className={cn('text-2xl font-bold mt-1', updatesCount > 0 ? 'text-yellow-600' : '')}>
              {updatesCount}
            </p>
          </CardContent>
        </Card>
      </div>

      {statusFilter !== 'all' && (
        <p className="text-xs text-muted-foreground -mt-1">
          Showing {statusFilter === 'updates' ? 'stations with pending Windows updates' : statusFilter} stations only —{' '}
          <button className="underline hover:text-foreground" onClick={() => setStatusFilter(statusFilter)}>
            clear filter
          </button>
        </p>
      )}

      {/* Station grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="h-56 animate-pulse bg-gray-100 dark:bg-gray-800" />
          ))}
        </div>
      ) : filteredStations.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Monitor className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-1">No stations found</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              Download the AOI Demo Agent and install it on a display PC. It will automatically register here on first run.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredStations.map((station) => (
            <StationCard
              key={station.id}
              station={station}
              latestVersion={latestRelease?.version ?? null}
              latestRelease={latestRelease ?? null}
              userName={userName}
              onCommand={handleCommand}
              onEdit={setEditStation}
              onToggleVideo={(id, enabled) => videoToggleMutation.mutate({ stationIds: [id], enabled })}
              onHudPush={(id, buildId) => hudMutation.mutate({ stationId: id, buildId })}
              onHudToggle={(id, enabled) => hudToggleMutation.mutate({ stationId: id, enabled })}
              onAdToggle={(id, enabled) => adToggleMutation.mutate({ stationId: id, enabled })}
              onDemoMode={(s, enter) => demoModeMutation.mutate({ station: s, enter })}
                onAdminMode={(s, enter) => adminModeMutation.mutate({ station: s, enter })}
                onEnableUsb={(sid, enable) => usbUnlockMutation.mutate({ stationId: sid, enable })}
              onDelete={setDeleteTarget}
              canSend={canSend}
            />
          ))}
        </div>
      )}

      {/* Video control */}
      <VideoControlCard
        stations={stations}
        locations={locations}
        onToggle={(stationIds, enabled) => videoToggleMutation.mutate({ stationIds, enabled })}
      />

      {/* Agent updates + downloads — admin-only (marketing doesn't install agents) */}
      {isAdmin && (
        <>
          <AgentUpdatesCard
            stations={filteredStations}
            currentRelease={latestRelease ?? null}
            publishedBy={userName}
            onPushAll={() => {
              if (!latestRelease) return;
              const outdated = filteredStations.filter((s) => s.agent_version !== latestRelease.version);
              if (outdated.length === 0) return;
              updateAgentMutation.mutate({ stationIds: outdated.map((s) => s.id), release: latestRelease });
            }}
            isPushingAll={updateAgentMutation.isPending}
          />

          <AgentDownloadCard release={latestRelease} history={releaseHistory} />
        </>
      )}

      {/* Manage shops dialog */}
      <ManageShopsDialog open={manageShops} onClose={() => setManageShops(false)} />

      {/* Edit dialog */}
      <EditStationDialog
        station={editStation}
        locations={locations}
        builds={builds as any}
        canSetAdminPolicy={isAdmin}
        onClose={() => setEditStation(null)}
        onSave={(id, updates) => updateMutation.mutate({ id, updates, prevBuildId: editStation?.build_id })}
      />

      {/* Confirm command dialog */}
      {logsModal && (
        <LogsModal
          stationId={logsModal.stationId}
          stationName={logsModal.stationName}
          onClose={() => setLogsModal(null)}
        />
      )}

      {pendingCommand && pendingCfg && (
        <ConfirmDialog
          open
          title={pendingCfg.title}
          description={pendingCfg.description}
          confirmLabel={pendingCfg.label}
          destructive={pendingCommand.cmd === 'retire'}
          onConfirm={confirmCommand}
          onCancel={() => setPendingCommand(null)}
        />
      )}

      {/* Delete record dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Remove station record?"
        description={`This removes "${deleteTarget?.name}" from the dashboard. The agent on the PC will continue running until manually uninstalled.`}
        confirmLabel="Remove"
        destructive
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
